import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "@remix-run/react";
import { AlertTriangle, CircleCheckBig } from "lucide-react";

import { Dialog, DialogContent } from "~/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/ui/form";
import { Input } from "~/components/ui/input";

import {
  LEAD_FORMS,
  LEAD_STATUS,
  trackAddListingRequestConversion,
  trackLead,
  trackFormOpen,
} from "~/lib/ConversionTracking";
import EmailService from "~/lib/services/EmailService";
import HomeServices from "~/lib/services/HomeServices";
import ListingsService from "~/lib/services/ListingsService";
import SalesForceServices from "~/lib/services/SalesForceServices";
import { useCurrentRegion } from "~/lib/helpers/CurrencyHelper";
import { getCurrentCustomerCount } from "~/lib/helpers/StringHelper";
import {
  getSalesforceAdClickIds,
  getLandingPageUrl,
} from "~/lib/helpers/adClickIds";
import { getCountryData, getRegionEmail } from "custom-config/region.configs";
import {
  isCommonFreeEmail,
  WORK_EMAIL_VALIDATION_MESSAGE,
} from "~/lib/helpers/commonFreeEmail";
import { HappyClientsAvatars } from "../common/HappyClientsAvatars";

import PhoneInput from "react-phone-input-2";
import { useForm } from "react-hook-form";

type FormValues = {
  firstName: string;
  lastName: string;
  email: string;
  accountName: string;
  mobilePhone?: string;
};

type SubmitMessage =
  | null
  | {
    icon: React.ReactNode;
    title: string;
    description: string;
    type: "success" | "error";
  };

type Step2EmailVerifyState = {
  pending: boolean;
  error: string | null;
  freemailHint: string | null;
};

function parseEmailVerifyResult(data: unknown) {
  const payload = (data as any)?.data ?? data;
  const isFreemailRaw =
    (payload as any)?.["is-freemail"] ??
    (payload as any)?.isFreemail ??
    (payload as any)?.["is_freemail"];
  const isFreemail =
    typeof isFreemailRaw === "string"
      ? isFreemailRaw.toLowerCase() === "true"
      : Boolean(isFreemailRaw);
  const isVerified =
    Boolean((payload as any)?.verified) || Boolean((payload as any)?.valid);
  return { isFreemail, isVerified };
}

function RightHero() {
  return (
    <div className="relative min-h-[520px] md:min-h-[640px] h-full">
      <img
        src={`${process.env.PUBLIC_REMIX_APP_ASSESTS_IMAGES}/assets/add-listing/add-listing.avif`}
        alt="Office space"
        className="absolute inset-0 h-full w-full object-cover"
      />
      <div className="absolute inset-0 bg-black/5" />

      <div className="absolute  top-11 left-1/2 -translate-x-1/2 flex items-center justify-center">
        <div className="relative w-[520px] max-w-[92%]">
          <img
            src={`${process.env.PUBLIC_REMIX_APP_ASSESTS_IMAGES}/assets/add-listing/add-listing-card.avif`}
            alt="Add listing preview card"
            className="w-full h-auto drop-shadow-2xl"
            loading="lazy"
            decoding="async"
          />
        </div>
      </div>
    </div>
  );
}

function CustomerRatingRow({ regionRating }: { regionRating: string }) {
  return (
    <div className="flex items-center gap-3">
      <HappyClientsAvatars className="flex -space-x-2" imageClassName="h-8 w-8" />

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-semibold text-gray-900">
          {getCurrentCustomerCount()}+ Happy Customers
        </span>
        <div className="flex items-center bg-white/90 backdrop-blur px-2.5 py-1 rounded-lg text-sm font-semibold text-gray-900 shadow-sm border border-gray-100">
          <img
            src={`${process.env.PUBLIC_REMIX_APP_ASSESTS_IMAGES}/assets/home-page/google-fav-logo.avif`}
            alt="Google"
            title="Google"
            className="w-5 h-5 mr-2"
            width={20}
            height={20}
            loading="lazy"
          />
          ⭐ {regionRating}
        </div>
      </div>
    </div>
  );
}

export default function AddListingTwoStepModal({
  onClose,
  initialStep = 1,
  initialEmail = "",
}: {
  onClose: () => void;
  initialStep?: 1 | 2;
  initialEmail?: string;
}) {
  // Every "Add Listing" CTA is a button that opens this, never a link, so the
  // delegated link listener never sees it. This component only renders when
  // it is shown, so mounting is the open.
  useEffect(() => {
    trackFormOpen(LEAD_FORMS.ADD_LISTING);
  }, []);

  const prefilledEmail = initialEmail.trim();
  const [step, setStep] = useState<1 | 2>(initialStep === 2 ? 2 : 1);
  const [loading, setLoading] = useState(false);
  const [submitMessage, setSubmitMessage] = useState<SubmitMessage>(null);
  const [submitUrl, setSubmitUrl] = useState<string | null>(null);
  const [phoneCountry, setPhoneCountry] = useState<string>("");
  const [step1Email, setStep1Email] = useState(prefilledEmail);
  const [step1EmailError, setStep1EmailError] = useState<string>("");
  const [step1EmailVerifying, setStep1EmailVerifying] = useState(false);
  const [step2EmailVerify, setStep2EmailVerify] = useState<Step2EmailVerifyState>(
    { pending: false, error: null, freemailHint: null },
  );
  const emailVerifyRequestId = useRef(0);
  const submitInFlightRef = useRef(false);
  /** When this matches the input value, email onChange must not bump request id or clear verify state (RHF sync from step 1). */
  const step2EmailSkipInvalidationRef = useRef<string>("");
  const partialCloseSubmitSentRef = useRef(false);
  const [regionRating, setRegionRating] = useState<string>("5.0");

  const regionConfig = useCurrentRegion();
  const regionCode = regionConfig?.region || "us";
  const countryData = useMemo(() => getCountryData(regionCode), [regionCode]);
  const supportPhoneHref = countryData?.phoneNumberWithCode || "";
  const supportPhoneLabel =
    countryData?.phoneNumberWithCode || countryData?.phoneNumber || "";
  const supportEmail = useMemo(() => getRegionEmail(regionCode), [regionCode]);

  const phoneDefaultCountry = useMemo(() => {
    const region = regionConfig?.region;
    if (!region) return undefined;
    return region === "uk" ? "gb" : region;
  }, [regionConfig?.region]);

  useEffect(() => {
    const region = regionConfig?.region;
    if (!region) return;

    let cancelled = false;
    HomeServices.getHomeSummary(region)
      .then((data: any) => {
        const raw = data?.sumRatings;
        const num = Number.parseFloat(String(raw ?? ""));
        const rating = Number.isFinite(num) && num > 0 ? num.toFixed(2) : "5.0";
        if (!cancelled) setRegionRating(rating);
      })
      .catch(() => {
        if (!cancelled) setRegionRating("5.0");
      });

    return () => {
      cancelled = true;
    };
  }, [regionConfig?.region]);

  const [searchParams] = useSearchParams();
  const device = searchParams.get("device") || "";
  const keywords = searchParams.get("keyword") || "";
  const campaign = searchParams.get("campaign") || "";

  const form = useForm<FormValues>({
    defaultValues: {
      firstName: "",
      lastName: "",
      email: prefilledEmail,
      accountName: "",
      mobilePhone: "",
    },
    mode: "onSubmit",
    reValidateMode: "onBlur",
  });

  useEffect(() => {
    if (initialStep !== 2 || !prefilledEmail) return;
    step2EmailSkipInvalidationRef.current = prefilledEmail;
  }, [initialStep, prefilledEmail]);

  async function runBackgroundEmailVerification(email: string): Promise<boolean> {
    const trimmed = email.trim();
    if (!trimmed) {
      setStep2EmailVerify({
        pending: false,
        error: "Enter your Email",
        freemailHint: null,
      });
      return false;
    }

    const requestId = ++emailVerifyRequestId.current;
    if (isCommonFreeEmail(trimmed)) {
      setStep2EmailVerify({
        pending: false,
        error: WORK_EMAIL_VALIDATION_MESSAGE,
        freemailHint: null,
      });
      return false;
    }

    setStep2EmailVerify({
      pending: true,
      error: null,
      freemailHint: null,
    });

    try {
      const data = await EmailService.verifyEmail(encodeURIComponent(trimmed));
      if (requestId !== emailVerifyRequestId.current) return false;

      const { isFreemail, isVerified } = parseEmailVerifyResult(data);

      if (isFreemail || !isVerified) {
        setStep2EmailVerify({
          pending: false,
          error: "Please enter a valid business email address",
          freemailHint: null,
        });
        return false;
      }

      setStep2EmailVerify({ pending: false, error: null, freemailHint: null });
      return true;
    } catch {
      if (requestId !== emailVerifyRequestId.current) return false;
      setStep2EmailVerify({
        pending: false,
        error: "Could not validate email. Try again.",
        freemailHint: null,
      });
      return false;
    }
  }

  async function goToStep2() {
    const nextEmail = step1Email.trim();
    setStep1EmailError("");

    if (!nextEmail) {
      setStep1EmailError("Enter your business email to continue.");
      return;
    }

    if (isCommonFreeEmail(nextEmail)) {
      setStep1EmailError(WORK_EMAIL_VALIDATION_MESSAGE);
      return;
    }

    setStep1EmailVerifying(true);
    try {
      const data = await EmailService.verifyEmail(encodeURIComponent(nextEmail));
      const { isFreemail, isVerified } = parseEmailVerifyResult(data);

      if (isFreemail || !isVerified) {
        setStep1EmailError("Please enter a valid business email address");
        return;
      }

      form.setValue("email", nextEmail, { shouldValidate: false });
      step2EmailSkipInvalidationRef.current = nextEmail;
      setStep(2);
    } catch {
      setStep1EmailError("Could not validate email. Try again.");
    } finally {
      setStep1EmailVerifying(false);
    }
  }

  useEffect(() => {
    if (step !== 2 || submitMessage) return;
    const email =
      form.getValues("email")?.trim() || step1Email.trim();
    if (!email) return;
    void runBackgroundEmailVerification(email);
  }, [step, submitMessage]);

  async function executeAddListingSubmit(values: FormValues) {
    setSubmitMessage(null);
    setSubmitUrl(null);

    const payload = {
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      accountName: values.accountName,
      mobilePhone: `+${values.mobilePhone}`,
      country: phoneCountry || regionConfig?.country,
      formUrl: typeof window !== "undefined" ? window.location.href : "",
      ...getSalesforceAdClickIds(),
      device,
      ppcKeywords: keywords,
      campaign,
      userLandingUrl: getLandingPageUrl(),
    };

    try {
      const response = await SalesForceServices.addListing({ contact: payload });

      if (response?.errorCode === "200") {
        trackAddListingRequestConversion({
          email: payload.email,
          phone_number: payload.mobilePhone,
        });
        trackLead({
          form: LEAD_FORMS.ADD_LISTING,
          status: LEAD_STATUS.COMPLETE,
          userData: {
            email: payload.email,
            phone_number: payload.mobilePhone,
          },
        });

        ListingsService.sendSuccessNotification(
          { ...payload, sfApiResponse: response },
          "add-listing"
        );

        setSubmitMessage({
          icon: (
            <CircleCheckBig className="h-6 w-6 text-gray-700" strokeWidth={2} />
          ),
          title: "Let’s create your workspace and listings!",
          description:
            "MyOfficeHub is your dedicated dashboard exclusive to Office Hub where you can manage, create and edit your properties, listings and leads. From sales insights to adding a listing, everything you need to manage your workspace is now one click away.",
          type: "success",
        });
      } else {
        ListingsService.sendErrorNotification(
          { ...payload, sfApiResponse: response },
          "add-listing"
        );

        setSubmitMessage({
          icon: <AlertTriangle className="h-5 w-5 text-gray-700" />,
          title: "This email is already registered.",
          description:
            "This email is already associated with an account. Sign in or reset your password to continue.",
          type: "error",
        });
      }

      setSubmitUrl(response?.myOHUrl ?? null);
    } catch (err: any) {
      setSubmitMessage({
        icon: <AlertTriangle className="h-5 w-5 text-gray-700" />,
        title: "Something went wrong.",
        description: err?.message ? `Error: ${err.message}` : "Please try again.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  }

  async function onStep2SubmitAttempt() {
    if (submitInFlightRef.current) return;
    submitInFlightRef.current = true;
    setLoading(true);
    try {
      const values = form.getValues();
      const nonEmailOk = await form.trigger(
        ["firstName", "lastName", "mobilePhone", "accountName"],
        { shouldFocus: true },
      );
      if (!nonEmailOk) return;
      if (!values.email?.trim()) {
        form.setError("email", {
          type: "required",
          message: "Enter your Email",
        });
        return;
      }
      form.clearErrors("email");
      const emailAccepted = await runBackgroundEmailVerification(values.email);
      if (!emailAccepted) return;
      await executeAddListingSubmit(values);
    } finally {
      submitInFlightRef.current = false;
      setLoading(false);
    }
  }

  async function submitPartialOnCloseIfNeeded() {
    if (partialCloseSubmitSentRef.current) return;
    if (loading) return;
    const v = form.getValues();
    const email = (v.email || step1Email).trim();
    if (!email) return;
    if (submitMessage?.type === "success") return;

    partialCloseSubmitSentRef.current = true;
    try {
      const userLandingUrl = getLandingPageUrl();
      const payload = {
        firstName: (v.firstName ?? "").trim(),
        lastName: (v.lastName ?? "").trim(),
        email,
        accountName: (v.accountName ?? "").trim(),
        mobilePhone: v.mobilePhone ? `+${v.mobilePhone}` : "",
        country: phoneCountry || regionConfig?.country,
        formUrl: typeof window !== "undefined" ? window.location.href : "",
        ...getSalesforceAdClickIds(),
        device,
        ppcKeywords: keywords,
        campaign,
        userLandingUrl,
      };
      const response = await SalesForceServices.addListing({ contact: payload });
      if (response?.errorCode === "200") {
        trackLead({
          form: LEAD_FORMS.ADD_LISTING,
          status: LEAD_STATUS.PARTIAL,
          userData: {
            email: payload.email,
            phone_number: payload.mobilePhone,
          },
        });
      }
    } catch {
      partialCloseSubmitSentRef.current = false;
    }
  }

  function handleDialogOpenChange(open: boolean) {
    if (!open) {
      void submitPartialOnCloseIfNeeded();
      onClose();
    }
  }

  return (
    <div className="flex justify-center py-12">
      <Dialog open onOpenChange={handleDialogOpenChange}>
        <DialogContent
          className="p-0 bg-transparent border-none overflow-visible w-[95vw] max-w-[980px]"
        >
          <div
            className={`w-full grid grid-cols-1 ${step === 2 && submitMessage ? "" : "md:grid-cols-2"} rounded-[28px] shadow-2xl overflow-hidden bg-white`}
          >
            {/* Left */}
            <div className="p-6 sm:p-10 bg-white">
              {step === 1 ? (
                <>
                  <div className="hidden md:block">
                    <CustomerRatingRow regionRating={regionRating} />
                  </div>

                  <h2 className="mt-7 text-[40px] md:text-[44px] font-extrabold leading-[1.05] text-oh-primary">
                    Rent Out Your <br />
                    Spare Office Space
                  </h2>
                  <p className="mt-4 text-[14px] text-gray-800 max-w-[360px]">
                    Join the world’s biggest marketplace for flexible and shared
                    office space.
                  </p>

                  <div className="mt-10 flex items-center  max-w-[420px]">
                    <input
                      value={step1Email}
                      onChange={(e) => {
                        setStep1Email(e.target.value);
                        if (step1EmailError) setStep1EmailError("");
                      }}
                      placeholder="Enter your work email"
                      className="h-11 w-full bg-[#fafafa] rounded-md rounded-r-none border border-gray-200 px-4 text-sm outline-none focus:border-gray-400"
                      type="email"
                      autoComplete="email"
                    />
                    <button
                      type="button"
                      onClick={() => void goToStep2()}
                      disabled={step1EmailVerifying}
                      className="h-11 shrink-0 rounded-md rounded-l-none bg-gray-900 px-6 text-sm font-semibold text-white hover:bg-gray-800 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      Get Started
                    </button>
                  </div>
                  {step1EmailError ? (
                    <p className="mt-2 text-sm text-red-600 ">
                      {step1EmailError}
                    </p>
                  ) : null}


                  <div className="mt-10 text-sm text-gray-800">
                    <a
                      href="https://d3ec9r8yaget8k.cloudfront.net/site/assets/add-listing/OfficeHub-Global_Partnership_Agreement.pdf"
                      className="text-oh-primary underline underline-offset-2 font-semibold"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Download our Partnership Guide
                    </a>{" "}
                    <span className="text-gray-800">for full T&amp;C’s</span>
                  </div>

                  <div className="mt-10 grid grid-cols-2 gap-x-6 gap-y-5 max-w-[520px]">
                    <div className="flex items-start gap-3">
                      <img
                        src="/assets/icons/number1.svg"
                        alt=""
                        className="mt-0.5 h-7 w-7 shrink-0"
                        loading="lazy"
                        decoding="async"
                      />
                      <p className="text-[13px] leading-snug text-gray-800">
                        World&apos;s No 1. shared office marketplace since 2014
                      </p>
                    </div>

                    <div className="flex items-start gap-3">
                      <img
                        src="/assets/icons/host.svg"
                        alt=""
                        className="mt-0.5 h-7 w-7 shrink-0"
                        loading="lazy"
                        decoding="async"
                      />
                      <p className="text-[13px] leading-snug text-gray-800">
                        List for{" "}
                        <span className="text-oh-secondary font-semibold">FREE</span>, no upfront fees
                      </p>
                    </div>

                    <div className="flex items-start gap-3">
                      <img
                        src="/assets/icons/happy-client.svg"
                        alt=""
                        className="mt-0.5 h-7 w-7 shrink-0"
                        loading="lazy"
                        decoding="async"
                      />
                      <p className="text-[13px] leading-snug text-gray-800">
                        {getCurrentCustomerCount()}+ happy clients across 25 countries
                      </p>
                    </div>



                    <div className="flex items-start gap-3">
                      <img
                        src="/assets/icons/rating.svg"
                        alt=""
                        className="mt-0.5 h-7 w-7 shrink-0"
                        loading="lazy"
                        decoding="async"
                      />
                      <p className="text-[13px] leading-snug text-gray-800">
                        Our hosts have earned over $200M USD
                      </p>
                    </div>
                  </div>





                  <div className="mt-8 md:hidden">
                    <CustomerRatingRow regionRating={regionRating} />
                  </div>
                </>
              ) : (
                <div className="flex flex-col">
                  {!submitMessage ? (
                    <>
                      <div className="mt-4 hidden md:block">
                        <CustomerRatingRow regionRating={regionRating} />
                      </div>

                      <h2 className="mt-7 text-[40px] md:text-[44px] font-extrabold leading-[1.05] text-oh-primary">
                        Rent Out Your <br />
                        Spare Office Space
                      </h2>
                      <p className="mt-4 text-[14px] text-gray-800 max-w-[360px]">
                        Join the world’s biggest marketplace for flexible and shared
                        office space.
                      </p>

                      <Form {...form}>
                        <form
                          onSubmit={(e) => {
                            e.preventDefault();
                            void onStep2SubmitAttempt();
                          }}
                          className="mt-6 space-y-4"
                        >
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <FormField
                              control={form.control}
                              name="firstName"
                              rules={{ required: "Enter your First Name" }}
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel className="text-gray-900">
                                    First Name
                                  </FormLabel>
                                  <FormControl>
                                    <Input
                                      placeholder="First Name"
                                      {...field}
                                      className="h-9 text-sm rounded-md"
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            <FormField
                              control={form.control}
                              name="lastName"
                              rules={{ required: "Enter your Last Name" }}
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel className="text-gray-900">
                                    Last Name
                                  </FormLabel>
                                  <FormControl>
                                    <Input
                                      placeholder="Last Name"
                                      {...field}
                                      className="h-9 text-sm rounded-md"
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>

                          <FormField
                            control={form.control}
                            name="email"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-gray-900">
                                  Email Address
                                </FormLabel>
                                <FormControl>
                                  <Input
                                    type="email"
                                    placeholder="Work email (name@company.com)"
                                    autoComplete="email"
                                    {...field}
                                    onChange={(e) => {
                                      const next = e.target.value;
                                      field.onChange(e);
                                      if (next === step2EmailSkipInvalidationRef.current) {
                                        return;
                                      }
                                      step2EmailSkipInvalidationRef.current = next;
                                      emailVerifyRequestId.current += 1;
                                      setStep2EmailVerify({
                                        pending: false,
                                        error: null,
                                        freemailHint: null,
                                      });
                                      form.clearErrors("email");
                                    }}
                                    onBlur={(e) => {
                                      field.onBlur();
                                      if (submitInFlightRef.current) return;
                                      step2EmailSkipInvalidationRef.current =
                                        e.target.value;
                                      void runBackgroundEmailVerification(
                                        e.target.value,
                                      );
                                    }}
                                    className="h-9 text-sm rounded-md"
                                  />
                                </FormControl>
                                {step2EmailVerify.error ? (
                                  <p className="text-sm text-red-600">
                                    {step2EmailVerify.error}
                                  </p>
                                ) : null}
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="mobilePhone"
                            rules={{ required: "Enter your Mobile Number" }}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-gray-900 text-xs">
                                  Mobile Number
                                </FormLabel>
                                <FormControl>
                                  <PhoneInput
                                    country={phoneDefaultCountry as any}
                                    value={field.value}
                                    onChange={(value, data) => {
                                      field.onChange(value);
                                      setPhoneCountry((data as any)?.name || "");
                                    }}
                                    inputStyle={{
                                      width: "100%",
                                      paddingLeft: 60,
                                      borderColor: "#e6e6e6",
                                      height: "36px",
                                    }}
                                    buttonStyle={{
                                      width: 50,
                                      borderColor: "#e6e6e6",
                                    }}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="accountName"
                            rules={{ required: "Enter your Company" }}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-gray-900">
                                  Company
                                </FormLabel>
                                <FormControl>
                                  <Input
                                    placeholder="Please enter your company name"
                                    {...field}
                                    className="h-9 text-sm rounded-md"
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <button
                            type="submit"
                            disabled={loading}
                            className="mt-2 w-full h-11 bg-gray-900 text-white rounded-md text-sm font-semibold hover:bg-gray-800 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {loading ? "Submitting..." : "SUBMIT"}
                          </button>

                          <p className="mt-3 text-xs text-gray-700 text-center leading-5">
                            I consent to having &quot;Office Hub Pty Limited&quot; collect my name,
                            email and phone number.
                          </p>
                        </form>
                      </Form>

                      <div className="mt-8 md:hidden">
                        <CustomerRatingRow regionRating={regionRating} />
                      </div>
                    </>
                  ) : (
                    <div className="bg-white rounded-lg p-0">
                      <div className="flex items-center space-x-2">
                        {submitMessage.icon}
                        <h2 className="text-gray-900 font-semibold text-lg">
                          {submitMessage.title}
                        </h2>
                      </div>
                      <p className="mt-2 text-sm text-gray-600">
                        {submitMessage.description}
                      </p>

                      <div className="mt-6 flex justify-end space-x-4">
                        {submitMessage.type === "error" && submitUrl && (
                          <Link
                            to={`${submitUrl}?resetpassword=true`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm font-medium text-gray-800 hover:underline"
                          >
                            Reset password
                          </Link>
                        )}

                        {submitUrl && (
                          <Link
                            to={submitUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-4 py-2 bg-black text-white text-sm rounded-md hover:bg-gray-800"
                          >
                            {submitMessage.type === "success"
                              ? "Get Started"
                              : "Login"}
                          </Link>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Right (keep mounted to avoid flicker) */}
            <div
              className={`${step === 2 && submitMessage ? "hidden" : "hidden md:block"}`}
            >
              <RightHero />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
